import React from "react";
import Button, { ButtonThemes } from "../../../ui-components/button/button";
import Tooltip from "./Tooltip";
import { TrendComparison } from "app/data/terminal-insights/models";
import TrendTooltip from "./TrendTooltip";

interface DashboardCardProps {
  icon: React.ReactNode;
  title: string;
  totalValue: number | string;
  totalLabel: string;
  metrics: Array<{
    label: string;
    value: number | string;
    trend?: {
      vs_yesterday: TrendComparison | null;
      vs_last_week?: TrendComparison | null;
      metricKey?: string;
      higher_is_better?: boolean;
    };
  }>;
  onShowMore?: () => void;
  info?: string[];
  onTrendClick?: (metricKey: string, metricLabel: string) => void;
}

const DashboardCard: React.FC<DashboardCardProps> = ({ 
  icon, 
  title, 
  totalValue, 
  totalLabel, 
  metrics, 
  onShowMore,
  info,
  onTrendClick
}) => {
  const getTrendIcon = (trend: "UP" | "DOWN" | "STABLE") => {
    switch (trend) {
      case "UP": return "↗";
      case "DOWN": return "↘";
      case "STABLE": return "→";
      default: return "→";
    }
  };

  const getTrendColor = (trend: "UP" | "DOWN" | "STABLE", higherIsBetter: boolean) => {
    if (trend === "STABLE") return "#F39C12";
    
    if (higherIsBetter) {
      return trend === "UP" ? "#27AE60" : "#E74C3C";
    } else {
      return trend === "DOWN" ? "#27AE60" : "#E74C3C";
    }
  };

  const getTrendBackground = (trend: "UP" | "DOWN" | "STABLE", higherIsBetter: boolean) => {
    if (trend === "STABLE") return "rgba(243, 156, 18, 0.1)";
    
    if (higherIsBetter) {
      return trend === "UP" ? "rgba(39, 174, 96, 0.1)" : "rgba(231, 76, 60, 0.1)";
    } else {
      return trend === "DOWN" ? "rgba(39, 174, 96, 0.1)" : "rgba(231, 76, 60, 0.1)";
    }
  };

  return (
    <div className="dashboard-card">
      <div className="dashboard-card__header">
        <div className="dashboard-card__icon">
          {icon}
        </div>
        <div className="dashboard-card__header-content">
          <span className="dashboard-card__total-label">{title}</span>
          <span className="dashboard-card__total-value">{totalValue} {totalLabel}</span>
        </div>
        {info && <Tooltip tooltips={info} />}
      </div>

      <div className="dashboard-card__metrics">
        {metrics.map((metric, index) => (
          <div key={index} className="dashboard-card__metric">
            <span className="dashboard-card__metric-label">{metric.label}:</span>
            <div className="dashboard-card__metric-value-row">
              <span className="dashboard-card__metric-value">{metric.value}</span>
              {metric.trend && metric.trend.vs_yesterday && (
                <TrendTooltip
                  vsYesterday={metric.trend.vs_yesterday}
                  vsLastWeek={metric.trend.vs_last_week}
                  higherIsBetter={metric.trend.higher_is_better ?? true}
                  metricKey={metric.trend.metricKey}
                  getTrendColor={getTrendColor}
                  getTrendIcon={getTrendIcon}
                >
                  <span 
                    className="dashboard-card__metric-trend"
                    style={{ 
                      color: getTrendColor(metric.trend.vs_yesterday.trend, metric.trend.higher_is_better ?? true),
                      backgroundColor: getTrendBackground(metric.trend.vs_yesterday.trend, metric.trend.higher_is_better ?? true)
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      
                      if (metric.trend?.metricKey && onTrendClick) {
                        onTrendClick(metric.trend.metricKey, metric.label);
                      }
                    }}
                  >
                    {getTrendIcon(metric.trend.vs_yesterday.trend)} {Math.abs(metric.trend.vs_yesterday.change_pct).toFixed(1)}%
                  </span>
                </TrendTooltip>
              )}
            </div>
          </div>
        ))}
      </div>

      {!!onShowMore && (
        <div className="dashboard-card__footer">
          <Button 
            theme={ButtonThemes.transparent} 
            className="dashboard-card__show-more"
            onClick={onShowMore}
          >
            Show more ↗
          </Button>
        </div>
      )}
    </div>
  );
};

export default DashboardCard;
